import {
  Component,
  computed,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { SectionConfigsStore } from '../../../features/data-tables/section-configs.store';
import { SectionConfigApiService } from '../../../features/data-tables/section-config-api.service';
import { ManagedDataSectionsUiService } from '../../managed-data/managed-data-sections-ui.service';
import { PermissionService } from '../../../shared/services/permissions/permission.service';
import { RoleService } from '../../../shared/services/permissions-management/role.service';
import { ProjectService } from '../../../shared/services/project/project.service';
import { MyAuthService } from '../../../auth/auth.service';
import { Permissions, SectionConfigDto } from '@anessa/shared-models';
import {
  SidebarPopout,
  type SidebarPopoutGroup,
  type SidebarPopoutItem,
} from '../../sidebar-popout/sidebar-popout';
import {
  SharePermissionsDialog,
  type SharePermissionsDialogData,
} from '../../../shared/common/share-permissions-dialog/share-permissions-dialog';
import { Role } from '../../../shared/interfaces/role.interface';

@Component({
  selector: 'app-managed-data-popout',
  imports: [SidebarPopout, I18nPipe, I18nScopeReadyDirective],
  templateUrl: './managed-data-popout.html',
  providers: [provideI18nScope('ui/sidebar'), I18nScopedService],
})
export class ManagedDataPopout implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(SectionConfigsStore);
  private readonly sectionsUi = inject(ManagedDataSectionsUiService);
  private readonly permissionService = inject(PermissionService, { optional: true });
  private readonly projectService = inject(ProjectService);
  private readonly roleService = inject(RoleService);
  private readonly sectionApi = inject(SectionConfigApiService);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(MyAuthService);
  private readonly i18nService = inject(I18nScopedService);

  @Output() readonly popoutClose = new EventEmitter<void>();

  protected readonly activeSectionKey = signal<string | null>(null);
  protected readonly sortMode = signal<'group' | 'alpha'>('group');
  private allRoles: Role[] = [];

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.syncActiveSection(e.urlAfterRedirects));
  }

  protected readonly canEdit = computed(() => {
    this.permissionService?.lastResolvedPlantId();
    return (
      this.permissionService?.hasPermission(Permissions.EDIT_MANAGED_DATA) ??
      false
    );
  });

  protected readonly groups = computed<SidebarPopoutGroup[]>(() => {
    const sections = this.store.sections().filter((s) => s.visible);

    if (this.sortMode() === 'alpha') {
      return this.buildAlphaGroups(sections);
    }
    return this.buildGroupGroups(sections);
  });

  ngOnInit(): void {
    const plantId = this.projectService.singlePlantId ?? undefined;
    this.store.load(plantId).subscribe();
    this.syncActiveSection(this.router.url);
    firstValueFrom(this.roleService.getAllRoles()).then((roles) => (this.allRoles = roles));
  }

  private buildGroupGroups(sections: SectionConfigDto[]): SidebarPopoutGroup[] {
    const groupMap = new Map<string, SidebarPopoutItem[]>();

    for (const section of [...sections].sort((a, b) => a.order - b.order)) {
      const key = section.group || '—';
      const items = groupMap.get(key) ?? [];
      items.push(this.toItem(section));
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private buildAlphaGroups(sections: SectionConfigDto[]): SidebarPopoutGroup[] {
    const groupMap = new Map<string, SidebarPopoutItem[]>();

    for (const section of [...sections].sort((a, b) =>
      a.label.localeCompare(b.label),
    )) {
      const key = section.label[0]?.toUpperCase() ?? '?';
      const items = groupMap.get(key) ?? [];
      items.push(this.toItem(section));
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private toItem(section: SectionConfigDto): SidebarPopoutItem {
    const currentUserId = this.authService.ensuredUser()?._id;
    const ownerTooltip = this.i18nService.t('owner_icon_tooltip');
    const isOwner = !!currentUserId && section.createdBy === currentUserId;
    return {
      id: section.key,
      label: section.label,
      labelIcon: isOwner ? 'person' : undefined,
      labelIconTooltip: isOwner ? ownerTooltip : undefined,
      showAction: section.accessLevel === 'admin',
      actionIcon: 'share',
      actionTooltip: this.i18nService.t('share'),
    };
  }

  protected toggleSortMode(): void {
    this.sortMode.update((m) => (m === 'group' ? 'alpha' : 'group'));
  }

  private syncActiveSection(url: string): void {
    const match = /\/data-management\/([^/?]+)/.exec(url);
    this.activeSectionKey.set(match ? decodeURIComponent(match[1]) : null);
  }

  protected onItemSelect(key: string): void {
    void this.router.navigate(['/data-management', key]);
  }

  protected openSectionManager(): void {
    this.sectionsUi.openSectionManagerDialog();
  }

  protected async onSectionShare(sectionKey: string): Promise<void> {
    const plantId = this.projectService.singlePlantId;
    if (!plantId) return;

    const section = this.store.sections().find((s) => s.key === sectionKey);
    if (!section) return;

    const currentPermissions = await firstValueFrom(
      this.sectionApi.getShare(section.id, plantId),
    );

    const dialogData: SharePermissionsDialogData = {
      title: this.i18nService.t('share_section'),
      itemName: section.label,
      currentPermissions,
      allRoles: this.allRoles,
      checkImpact: (keepRoleIds) =>
        firstValueFrom(this.sectionApi.getShareImpact(section.id, plantId, keepRoleIds)),
    };

    const ref = this.dialog.open<SharePermissionsDialog, SharePermissionsDialogData, typeof currentPermissions | null>(
      SharePermissionsDialog,
      { data: dialogData, width: '560px' },
    );

    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    await firstValueFrom(this.sectionApi.updateShare(section.id, plantId, { sharePermissions: result }));
    this.store.load(plantId).subscribe();
  }
}
